import ExcelJS from "exceljs";
import type { LvMatch } from "../utils/types";
import type { ExportGroupValues } from "./buildExportGroupValues";
import type { ExcelColumnHeaders, UnitLabels } from "./types";

const EUR_NUMBER_FORMAT = '#,##0.00 "€"';
const HOURS_NUMBER_FORMAT = "0.00";

const PIECE_UNIT_ALIASES = new Set(["pcs", "piece", "pieces"]);
const METER_UNIT_ALIASES = new Set(["m", "meter", "meters"]);

export const normalizeUnit = (
  rawUnit: string | undefined,
  unitLabels: UnitLabels,
): string => {
  if (!rawUnit) return "";
  const lowercased = rawUnit.toLowerCase().trim();
  if (PIECE_UNIT_ALIASES.has(lowercased)) return unitLabels.piece;
  if (METER_UNIT_ALIASES.has(lowercased)) return unitLabels.meters;
  return rawUnit;
};

export const resolveLv = (match?: LvMatch) => {
  if (!match?.lvId || match.confidence === "low") return "";
  return match.lvId;
};

export const formatMeters = (m: unknown) => {
  const n = Number(m) || 0;
  return `${Math.round(n)} m`;
};

export const formatQuantity = (q: unknown, unit?: string) => {
  const n = Number(q);
  if (unit && unit.toLowerCase().includes("m")) return formatMeters(n);
  if (isNaN(n)) return `${q} ${unit ?? ""}`.trim();
  const rounded = Math.round(n * 10) / 10;
  return `${String(rounded)}${unit ? ` ${unit}` : ""}`;
};

export const roundMeters = (meters: unknown): number => {
  const numericMeters = Number(meters);
  if (Number.isNaN(numericMeters)) return 0;
  return Math.round(numericMeters);
};

export const roundQuantity = (quantity: unknown): number => {
  const numericQuantity = Number(quantity);
  if (Number.isNaN(numericQuantity)) return 0;
  return Math.round(numericQuantity * 10) / 10;
};

export const buildWorkbook = (
  headers: ExcelColumnHeaders,
  includePrices: boolean,
): { workbook: ExcelJS.Workbook; sheet: ExcelJS.Worksheet } => {
  const workbook = new ExcelJS.Workbook();
  const sheet = workbook.addWorksheet(headers.sheetName);

  const priceColumns: Partial<ExcelJS.Column>[] = includePrices
    ? [
        {
          key: "MaterialPrice",
          width: 14,
          style: { numFmt: EUR_NUMBER_FORMAT },
        },
        { key: "Hours", width: 10, style: { numFmt: HOURS_NUMBER_FORMAT } },
        { key: "HourlyRate", width: 12, style: { numFmt: EUR_NUMBER_FORMAT } },
        { key: "UnitPrice", width: 14, style: { numFmt: EUR_NUMBER_FORMAT } },
      ]
    : [];

  sheet.columns = [
    { key: "Index", width: 6 },
    { key: "Group", width: 60 },
    { key: "Amount", width: 12 },
    { key: "Unit", width: 10 },
    ...priceColumns,
    { key: "LVPosition", width: 14 },
    { key: "Approved", width: 12 },
  ];

  return { workbook, sheet };
};

// ExcelJS drops keys without a column, so a sheet without price columns simply ignores these
export const priceCellsOf = (
  values: ExportGroupValues,
): Record<
  "MaterialPrice" | "Hours" | "HourlyRate" | "UnitPrice",
  number | undefined
> => ({
  MaterialPrice: values.materialPrice ?? undefined,
  Hours: values.hours ?? undefined,
  HourlyRate: values.hourlyRate ?? undefined,
  UnitPrice: values.unitPrice ?? undefined,
});

export const styleColumnHeaderRow = (row: ExcelJS.Row) => {
  row.height = 20;
  row.eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FF006400" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "FFE6FFEF" },
    };
    cell.alignment = { vertical: "middle", horizontal: "left" };
    cell.border = {
      bottom: { style: "thin", color: { argb: "FFBFBFBF" } },
    };
  });
  row.commit();
};

export const addColumnHeaderRow = (
  sheet: ExcelJS.Worksheet,
  headers: ExcelColumnHeaders,
) => {
  const row = sheet.addRow({
    Index: headers.index,
    Group: headers.group,
    Amount: headers.amount,
    Unit: headers.unit,
    MaterialPrice: headers.materialPrice,
    Hours: headers.hours,
    HourlyRate: headers.hourlyRate,
    UnitPrice: headers.unitPrice,
    LVPosition: headers.lvPosition,
    Approved: headers.approved,
  });
  styleColumnHeaderRow(row);
};

export const addSectionHeader = (
  sheet: ExcelJS.Worksheet,
  text: string,
  bgArgb: string,
  fontArgb: string,
) => {
  const rowNum = (sheet.lastRow?.number ?? 0) + 1;
  const row = sheet.getRow(rowNum);
  sheet.mergeCells(rowNum, 1, rowNum, sheet.columns.length);
  const cell = row.getCell(1);
  cell.value = text;
  cell.font = { bold: true, color: { argb: fontArgb } };
  cell.fill = {
    type: "pattern",
    pattern: "solid",
    fgColor: { argb: bgArgb },
  } as any;
  cell.alignment = { vertical: "middle", horizontal: "left" } as any;
  row.height = 18;
  row.commit();
};

export const downloadBlob = (blob: Blob, filename: string) => {
  const url = window.URL.createObjectURL(blob);
  const anchor = document.createElement("a");
  anchor.href = url;
  anchor.download = filename;
  document.body.appendChild(anchor);
  anchor.click();
  anchor.remove();
  window.URL.revokeObjectURL(url);
};

export const downloadWorkbook = async (
  workbook: ExcelJS.Workbook,
  filename: string,
) => {
  const buffer = await workbook.xlsx.writeBuffer();
  downloadBlob(
    new Blob([buffer], { type: "application/octet-stream" }),
    filename,
  );
};
